import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Plug, CheckCircle2, XCircle, RefreshCcw, Copy, Check, HelpCircle, ChevronDown, AlertCircle, ShieldCheck } from "lucide-react";

const Section = ({ title, children }) => (
  <div className="rounded-2xl bg-white border kt-line p-6" data-testid={`int-${title.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-3">{title}</div>
    {children}
  </div>
);

const Pill = ({ ok, label }) => (
  <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] kt-tracking uppercase ${
    ok ? "bg-[#EAF3EC] text-[#4A6D50] border-[#CDE0D3]" : "bg-[#F3F3F3] text-[#6F6B66] border-[#ECE7DF]"
  }`}>
    {ok ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
    {label}
  </span>
);

export default function AdminIntegrations() {
  const [ov, setOv] = useState(null);
  const [sh, setSh] = useState(null);
  const [form, setForm] = useState({});
  const [pricing, setPricing] = useState({ assessment_price_eur: "", salon_monthly_price_eur: "", salon_capped_price_eur: "", annual_discount_pct: "" });
  const [pricingSaving, setPricingSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [events, setEvents] = useState([]);
  const [webhookCopied, setWebhookCopied] = useState(false);
  const [variantHelpOpen, setVariantHelpOpen] = useState(false);
  const webhookUrl = `${API}/webhooks/shopify/orders-paid`;

  // Live numeric validation for Shopify Variant ID (must be a 10–20 digit number).
  const variantIdRaw = (form.variant_id ?? "").toString().trim();
  const variantIdError = variantIdRaw === ""
    ? "Required — paste the numeric Variant ID from Shopify."
    : !/^\d+$/.test(variantIdRaw)
      ? "Variant ID must be digits only (no letters, no gid://…, no /)."
      : variantIdRaw.length < 10
        ? "Looks too short — Shopify variant IDs are typically 13+ digits."
        : "";

  const load = async () => {
    const [ovR, shR, evR, apR, plR] = await Promise.all([
      axios.get(`${API}/admin/integrations`),
      axios.get(`${API}/admin/shopify/settings`),
      axios.get(`${API}/admin/shopify/events`),
      axios.get(`${API}/admin/assessment-price`),
      axios.get(`${API}/admin/plans/pro`),
    ]);
    setOv(ovR.data);
    setSh(shR.data);
    setEvents(evR.data.items || []);
    const tiers = plR.data.tiers || [];
    const base = tiers.find(t => t.id === "base_monthly") || {};
    const cap50 = tiers.find(t => t.id === "sessions_50") || {};
    setPricing({
      assessment_price_eur: apR.data.price_eur ?? 9.88,
      salon_monthly_price_eur: base.monthly_price_eur ?? 30.0,
      salon_capped_price_eur: cap50.monthly_price_eur ?? 30.0,
      annual_discount_pct: plR.data.annual_discount_pct ?? 15,
    });
    setForm({
      shop: shR.data.shop || "",
      variant_id: shR.data.variant_id || "",
      client_id: shR.data.client_id || "",
      collection_id: shR.data.collection_id || "",
      enabled: !!shR.data.enabled,
      issue_free_code_on_purchase: !!shR.data.issue_free_code_on_purchase,
      free_code_expires_days: shR.data.free_code_expires_days || 90,
      client_secret: "",
    });
  };
  useEffect(() => { load(); }, []);

  const savePricing = async () => {
    setPricingSaving(true);
    try {
      const plans = await axios.get(`${API}/admin/plans/pro`);
      const tiers = (plans.data.tiers || []).map(t => ({
        ...t,
        monthly_price_eur: t.id === "base_monthly"
          ? Number(pricing.salon_monthly_price_eur)
          : t.id === "sessions_50"
          ? Number(pricing.salon_capped_price_eur)
          : t.monthly_price_eur,
      }));
      await Promise.all([
        axios.patch(`${API}/admin/assessment-price`, { price_eur: Number(pricing.assessment_price_eur) }),
        axios.patch(`${API}/admin/plans/pro`, {
          annual_discount_pct: Number(pricing.annual_discount_pct),
          tiers,
        }),
      ]);
      toast.success("Prices saved");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setPricingSaving(false);
    }
  };

  const save = async () => {
    // Only enforce numeric validation when the checkout is actually enabled.
    if (form.enabled && variantIdError && !sh?.variant_id_from_env) {
      toast.error(`Fix the Variant ID: ${variantIdError}`);
      return;
    }
    setBusy(true);
    try {
      const payload = {
        collection_id: form.collection_id,
        enabled: form.enabled,
        issue_free_code_on_purchase: !!form.issue_free_code_on_purchase,
        free_code_expires_days: Number(form.free_code_expires_days) || 90,
      };
      // Only send credential fields when not sourced from env (server ignores them anyway if env is set).
      if (!sh?.shop_from_env) payload.shop = form.shop;
      if (!sh?.variant_id_from_env) payload.variant_id = form.variant_id;
      if (!sh?.client_id_from_env) payload.client_id = form.client_id;
      if (form.client_secret && !sh?.client_secret_from_env) payload.client_secret = form.client_secret;
      if (form.webhook_secret && !sh?.webhook_secret_from_env) payload.webhook_secret = form.webhook_secret;
      await axios.patch(`${API}/admin/shopify/settings`, payload);
      toast.success("Shopify settings saved");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const test = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/shopify/test`);
      if (r.data.ok) toast.success(`Connected: ${r.data.shop_name || "OK"}`);
      else toast.error(r.data.error || "Connection failed");
      await load();
    } finally { setBusy(false); }
  };

  const copyWebhook = async () => {
    await navigator.clipboard.writeText(webhookUrl);
    setWebhookCopied(true);
    toast.success("Webhook URL copied");
    setTimeout(() => setWebhookCopied(false), 2000);
  };

  if (!ov || !sh) return <div className="text-[#6F6B66]">…</div>;

  return (
    <div className="space-y-8" data-testid="admin-integrations">
      <div>
        <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-2 flex items-center gap-2">
          <Plug className="h-3.5 w-3.5" /> Integrations
        </div>
        <h1 className="font-serif text-3xl text-[#383633] tracking-tight">Connections</h1>
      </div>

      {/* Overview */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="rounded-2xl bg-white border kt-line p-5 flex items-center justify-between kt-shadow"
             data-testid="overview-shopify">
          <div>
            <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66]">Shopify · €9.88 checkout</div>
            <div className="text-sm text-[#383633] mt-1">{ov.shopify.message}</div>
          </div>
          <Pill ok={ov.shopify.connected && ov.shopify.enabled} label={ov.shopify.connected && ov.shopify.enabled ? "Live" : "Not live"} />
        </div>
        <div className="rounded-2xl bg-white border kt-line p-5 flex items-center justify-between kt-shadow"
             data-testid="overview-stripe">
          <div>
            <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66]">Stripe · Temporary backup</div>
            <div className="text-sm text-[#383633] mt-1">{ov.stripe.message}</div>
          </div>
          <Pill ok={ov.stripe.connected} label={ov.stripe.connected ? "Connected" : "Off"} />
        </div>
        <div className="rounded-2xl bg-white border kt-line p-5 flex items-center justify-between kt-shadow"
             data-testid="overview-odoo">
          <div>
            <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66]">Odoo CRM</div>
            <div className="text-sm text-[#383633] mt-1">{ov.odoo.message}</div>
          </div>
          <Pill ok={ov.odoo.connected} label={ov.odoo.connected ? "Connected" : "Not connected"} />
        </div>
        <div className="rounded-2xl bg-white border kt-line p-5 flex items-center justify-between kt-shadow"
             data-testid="overview-resend">
          <div>
            <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66]">Resend · Email delivery</div>
            <div className="text-sm text-[#383633] mt-1">{ov.resend.message}</div>
          </div>
          <Pill ok={ov.resend.connected} label={ov.resend.connected ? "Connected" : "Not connected"} />
        </div>
      </div>

      {/* Pricing controls — admin-editable */}
      <Section title="Pricing">
        <p className="text-sm text-[#6F6B66] mb-5">
          Individual assessment price + salon subscription tiers. Annual is auto-calculated as
          <span className="font-mono"> monthly × 12 × (1 − discount%)</span>.
        </p>
        <div className="grid sm:grid-cols-2 gap-4">
          <F label="Individual assessment price (EUR)">
            <Input
              type="number" step="0.01" min="0"
              value={pricing.assessment_price_eur}
              data-testid="pricing-individual"
              onChange={(e) => setPricing({ ...pricing, assessment_price_eur: e.target.value })} />
          </F>
          <F label="Salon monthly base — unlimited (EUR / month)">
            <Input
              type="number" step="0.01" min="0"
              value={pricing.salon_monthly_price_eur}
              data-testid="pricing-salon-monthly"
              onChange={(e) => setPricing({ ...pricing, salon_monthly_price_eur: e.target.value })} />
          </F>
          <F label="Salon monthly · 50-session cap (EUR / month)">
            <Input
              type="number" step="0.01" min="0"
              value={pricing.salon_capped_price_eur}
              data-testid="pricing-salon-capped"
              onChange={(e) => setPricing({ ...pricing, salon_capped_price_eur: e.target.value })} />
          </F>
          <F label="Annual pricing discount (%)">
            <Input
              type="number" step="1" min="0" max="40"
              value={pricing.annual_discount_pct}
              data-testid="pricing-annual-discount"
              onChange={(e) => setPricing({ ...pricing, annual_discount_pct: e.target.value })} />
          </F>
        </div>
        <div className="mt-3 text-[11px] text-[#6F6B66]">
          Annual preview: <span className="font-mono text-[#383633]">
            €{(Number(pricing.salon_monthly_price_eur || 0) * 12 * (1 - Number(pricing.annual_discount_pct || 0) / 100)).toFixed(2)}
          </span>
          &nbsp;/ year (unlimited tier).
        </div>
        <div className="mt-5">
          <Button onClick={savePricing} disabled={pricingSaving}
            data-testid="pricing-save"
            className="rounded-full bg-[#383633] hover:bg-[#4A4238] text-white text-xs kt-tracking uppercase px-6 h-11">
            Save pricing
          </Button>
        </div>
      </Section>

      {/* Shopify configuration */}
      <Section title="Shopify configuration">
        <div className="mb-5 flex items-center gap-3 flex-wrap">
          <Pill ok={sh.auth_mode === "client_credentials"}
            label={sh.auth_mode === "client_credentials"
              ? "Client Credentials Grant"
              : sh.auth_mode === "legacy_token"
                ? "Legacy shpat_ token"
                : "Not configured"} />
          <span className="text-[11px] text-[#6F6B66]">
            {sh.auth_mode === "client_credentials"
              ? "Access token is fetched programmatically and cached for 24h."
              : "Set SHOPIFY_CLIENT_ID + SHOPIFY_CLIENT_SECRET in the backend .env to migrate."}
          </span>
        </div>
        <p className="text-sm text-[#6F6B66] mb-5">
          Create a Shopify Dev Dashboard app (same organization as your store), enable Admin API scopes,
          then paste the Client ID + Client Secret into the backend <code>.env</code>. When both are set
          and this checkbox is on, the €9.88 unlock button routes to Shopify Checkout.
        </p>
        <div className="grid sm:grid-cols-2 gap-4">
          <F label={`Shop subdomain ${sh.shop_from_env ? "· from .env (read-only)" : "(e.g. g600wr-0h — without .myshopify.com)"}`}>
            <Input value={form.shop || ""}
              disabled={!!sh.shop_from_env}
              placeholder="g600wr-0h"
              data-testid="shopify-shop"
              onChange={(e) => setForm({ ...form, shop: e.target.value })} />
          </F>
          <F label="Variant ID (numeric) for the €9.88 SKU">
            <div className="relative">
              <Input value={form.variant_id}
                disabled={!!sh.variant_id_from_env}
                data-testid="shopify-variant-id"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="e.g. 16051752075646"
                aria-invalid={!!variantIdError}
                onChange={(e) => setForm({ ...form, variant_id: e.target.value.replace(/[^0-9]/g, "") })}
                className={variantIdError && !sh.variant_id_from_env ? "border-[#E8B5AC] focus-visible:ring-[#F0C7C0]" : ""}
              />
            </div>
            {sh.variant_id_from_env && (
              <div className="mt-2 text-[11px] text-[#6F6B66]">Sourced from SHOPIFY_VARIANT_ID (.env).</div>
            )}
            <button
              type="button"
              onClick={() => setVariantHelpOpen((v) => !v)}
              data-testid="shopify-variant-help-toggle"
              className="mt-2 inline-flex items-center gap-1.5 text-[11px] kt-tracking uppercase text-[#6F6B66] hover:text-[#383633]"
            >
              <HelpCircle className="h-3.5 w-3.5" />
              <span>How to find your Variant ID</span>
              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${variantHelpOpen ? "rotate-180" : ""}`} />
            </button>
            {variantIdError && !sh.variant_id_from_env && (
              <div data-testid="shopify-variant-error"
                className="mt-2 flex items-start gap-1.5 text-[11px] text-[#8A3B2E]">
                <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                <span>{variantIdError}</span>
              </div>
            )}
            {variantHelpOpen && (
              <div data-testid="shopify-variant-help"
                className="mt-3 rounded-xl border kt-line bg-[#F8F6F2] p-4 text-[12px] text-[#383633] leading-relaxed space-y-3">
                <div>
                  <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-1">Quickest way (product URL)</div>
                  <ol className="list-decimal ml-5 space-y-1">
                    <li>In Shopify Admin, go to <span className="font-medium">Products</span> and open the €9.88 assessment product.</li>
                    <li>Under <span className="font-medium">Variants</span>, click the specific variant that costs €9.88.</li>
                    <li>Look at the browser URL. It ends in <code className="bg-white border kt-line rounded px-1.5 py-0.5">/variants/<b>16051752075646</b></code> — copy those digits.</li>
                    <li>Paste them into the field above — digits only, no <code className="bg-white border kt-line rounded px-1.5 py-0.5">gid://</code>, no slashes.</li>
                  </ol>
                </div>
                <div className="text-[11px] text-[#6F6B66]">
                  <span className="font-medium text-[#383633]">Tip:</span> if you only see
                  <code className="bg-white border kt-line rounded px-1.5 py-0.5 mx-1">gid://shopify/ProductVariant/16051752075646</code>
                  keep just the trailing number.
                </div>
              </div>
            )}
          </F>
          <F label={`Client ID ${sh.client_id_from_env ? "· from .env (read-only)" : "· from Shopify Dev Dashboard"}`}>
            <Input value={form.client_id || ""}
              disabled={!!sh.client_id_from_env}
              placeholder="e.g. abc123def456..."
              data-testid="shopify-client-id"
              onChange={(e) => setForm({ ...form, client_id: e.target.value })} />
          </F>
          <F label={`Client Secret ${sh.client_secret_from_env ? "· from .env (read-only)" : sh.client_secret_set ? "· stored — leave blank to keep" : "· required"}`}>
            <Input value={form.client_secret || ""}
              disabled={!!sh.client_secret_from_env}
              type="password"
              placeholder={sh.client_secret_from_env ? "•••• from .env" : sh.client_secret_set ? "•••• stored" : "paste secret"}
              data-testid="shopify-client-secret"
              onChange={(e) => setForm({ ...form, client_secret: e.target.value })} />
          </F>
          <F label={`Webhook signing secret ${sh.webhook_secret_from_env ? "· from .env (read-only)" : sh.webhook_secret_set ? "· stored — leave blank to keep" : "· from Shopify Admin → Settings → Notifications"}`}>
            <Input value={form.webhook_secret || ""}
              disabled={!!sh.webhook_secret_from_env}
              type="password"
              placeholder={sh.webhook_secret_from_env ? "•••• from .env" : sh.webhook_secret_set ? "•••• stored" : "paste the Notifications 'Signing secret'"}
              data-testid="shopify-webhook-secret"
              onChange={(e) => setForm({ ...form, webhook_secret: e.target.value })} />
          </F>
          <div className="sm:col-span-2 rounded-2xl border kt-line bg-[#F8F6F2] p-4 text-[12px] text-[#383633] leading-relaxed" data-testid="shopify-webhook-info">
            <div className="flex items-start gap-3">
              <ShieldCheck className="h-4 w-4 mt-0.5 text-[#A8AD94] shrink-0" />
              <div>
                <div className="font-medium mb-1">Which secret does your webhook use?</div>
                <div className="text-[#6F6B66] space-y-1.5">
                  <div>
                    <span className="font-medium text-[#383633]">Webhooks created in Shopify Admin</span> (Settings → Notifications → Webhooks) are signed with the "Signing secret" shown at the top of that page — paste that value in the field above.
                  </div>
                  <div>
                    <span className="font-medium text-[#383633]">Webhooks registered via the Custom App API</span> are signed with the app's Client Secret. Leave the Webhook signing secret blank and we'll use Client Secret automatically.
                  </div>
                  <div className="pt-1">
                    Our endpoint tries <span className="font-medium">both</span> secrets, so a single Hairtu instance safely handles Admin-UI and Custom-App-created webhooks at the same time. HMAC verification stays strict — invalid signatures are rejected with 401.
                    {sh?.webhook_secret_set ? " (Signing secret currently stored.)" : ""}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <F label="Recommended-routine Collection ID (optional — limits €9.88 credit code)">
            <Input value={form.collection_id} data-testid="shopify-collection-id"
              onChange={(e) => setForm({ ...form, collection_id: e.target.value })} />
          </F>
          <label className="flex items-center gap-3 sm:col-span-2">
            <input type="checkbox" checked={!!form.enabled} data-testid="shopify-enabled"
              onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
              className="h-4 w-4" />
            <span className="text-sm text-[#383633]">Enable Shopify checkout (routes €9.88 payments)</span>
          </label>
        </div>

        <div className="mt-6 pt-5 border-t kt-line">
          <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-3">
            Free-assessment reward for KT Milano Shopify customers
          </div>
          <label className="flex items-start gap-3">
            <input type="checkbox" checked={!!form.issue_free_code_on_purchase}
              data-testid="shopify-free-code"
              onChange={(e) => setForm({ ...form, issue_free_code_on_purchase: e.target.checked })}
              className="h-4 w-4 mt-0.5" />
            <span className="text-sm text-[#383633]">
              Issue one unique free-assessment code (KT-FREE-XXXXXXXX, worth €9.88) to every KT Milano
              Shopify customer, delivered on the orders/paid webhook. The code unlocks one full Hairtu
              plan and follows the same paywall rules as complimentary codes.
            </span>
          </label>
          <div className="mt-3 max-w-xs">
            <Label className="text-xs kt-tracking uppercase text-[#6F6B66]">Expires after (days)</Label>
            <Input type="number" min="1" value={form.free_code_expires_days || 90}
              data-testid="shopify-free-code-days"
              onChange={(e) => setForm({ ...form, free_code_expires_days: e.target.value })}
              className="mt-2 border-[#ECE7DF] rounded-xl h-11" />
          </div>
        </div>

        <div className="mt-5 pt-4 border-t kt-line flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="text-[11px] text-[#6F6B66]">
            <div className="uppercase kt-tracking text-[10px] mb-1">Webhook URL to register in Shopify (orders/paid)</div>
            <code className="text-[#383633] break-all">{webhookUrl}</code>
          </div>
          <Button variant="outline" onClick={copyWebhook} data-testid="copy-webhook"
            className="rounded-full border-[#383633] text-[#383633] hover:bg-[#F8F6F2] text-xs kt-tracking uppercase h-10 shrink-0">
            {webhookCopied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            <span className="ml-2">{webhookCopied ? "Copied" : "Copy URL"}</span>
          </Button>
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-3 justify-end">
          <Button variant="outline" onClick={test} disabled={busy} data-testid="shopify-test"
            className="rounded-full border-[#383633] text-[#383633] hover:bg-[#F8F6F2] text-xs kt-tracking uppercase h-11">
            <RefreshCcw className="h-3.5 w-3.5" /><span className="ml-2">Test connection</span>
          </Button>
          <Button onClick={save} disabled={busy} data-testid="shopify-save"
            className="rounded-full bg-[#383633] hover:bg-[#4A4238] text-white h-11 px-6 text-xs kt-tracking uppercase">
            Save Shopify settings
          </Button>
        </div>

        {sh.last_error && (
          <div className="mt-4 rounded-xl border border-[#F0C7C0] bg-[#FCECEA] text-[#8A3B2E] text-xs p-3" data-testid="shopify-error">
            Last error: {sh.last_error}
          </div>
        )}
      </Section>

      {/* Shopify integration events */}
      <Section title="Shopify integration events (last 50)">
        <div className="max-h-72 overflow-auto border kt-line rounded-xl">
          <table className="w-full text-xs">
            <tbody>
              {events.length === 0 && (
                <tr><td className="p-4 text-center text-[#6F6B66]">No events yet.</td></tr>
              )}
              {events.map((e, i) => (
                <tr key={i} className="border-b kt-line last:border-0" data-testid="shopify-event-row">
                  <td className="p-2 font-mono text-[#383633]">{e.kind}</td>
                  <td className="p-2">{e.ok ? <Pill ok={true} label="OK" /> : <Pill ok={false} label="FAIL" />}</td>
                  <td className="p-2 text-[#6F6B66]">{(e.detail || "").slice(0, 120)}</td>
                  <td className="p-2 text-[#6F6B66] text-right whitespace-nowrap">{new Date(e.at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* Odoo + Resend info */}
      <div className="grid md:grid-cols-2 gap-4">
        <Section title="Odoo CRM">
          <div className="flex items-center justify-between mb-3">
            <Pill ok={ov.odoo.connected} label={ov.odoo.connected ? "Connected" : "Not connected"} />
          </div>
          <p className="text-sm text-[#6F6B66] leading-relaxed">
            When Odoo is connected, non-sensitive lead fields sync: name, email, language, score,
            profile, recommended routine, payment status, credit-code status. Individual quiz
            answers are never sent. Salon-client anonymous data is never sent.
          </p>
          <div className="text-[11px] text-[#6F6B66] mt-3">
            Odoo connection form appears here once we receive credentials from KT Milano.
          </div>
        </Section>
        <Section title="Resend · Transactional email">
          <div className="flex items-center justify-between mb-3">
            <Pill ok={ov.resend.connected} label={ov.resend.connected ? "Connected" : "Not connected"} />
          </div>
          <p className="text-sm text-[#6F6B66] leading-relaxed">
            EN + IT templates ready: assessment preview, paid-plan ready, credit issued, credit
            expiring, credit redeemed. Small KT Milano banner embedded. Partner ads only sent when
            marketing consent is granted. Delivery starts when a Resend API key + verified sender are configured.
          </p>
        </Section>
      </div>
    </div>
  );
}

function F({ label, children }) {
  return (
    <div>
      <Label className="text-xs kt-tracking uppercase text-[#6F6B66]">{label}</Label>
      <div className="mt-2 [&_input]:border-[#ECE7DF] [&_input]:rounded-xl [&_input]:h-11">{children}</div>
    </div>
  );
}
